import * as React from 'react';
// mui
import {
  CardHeader,
  Collapse,
  ListItemButton,
  TextField,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Button,
  Typography,
} from '@mui/material';
// icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FlagIcon from '@mui/icons-material/Flag';
import ClearIcon from '@mui/icons-material/Clear';
import EventIcon from '@mui/icons-material/Event';
import DoneIcon from '@mui/icons-material/Done'
// dayjs
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// types
import Event from '../../../interfaces/Event.interface';
// axios
import api from '../../../axios';
// components
import ItemBreadcrumbs from '../../common/ItemBreadcrumbs';
import { convertPriorityToString } from '../../common/propertyValueToString';
import ItemContentDate from '../../common/ItemContentDate';
import ExcuseDurationSelect from './ExcuseDurationSelect';

interface EventCardProps {
  eventData: Event;
  onMoveToNextTask: () => void;
}

export default function EventCard(props: EventCardProps) {
  // props
  const eventData: Event = props.eventData;
  const moveToNextTaskCallback = props.onMoveToNextTask;

  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // state
  const [ showExcuseForm, setShowExcuseForm ] = React.useState(false);
  const [ excuseValue, setExcuseValue ] = React.useState<String>('');
  const [ excuseLoading, setExcuseLoading ] = React.useState<boolean>(false);
  const [ completeLoading, setCompleteLoading ] = React.useState<boolean>(false);
  const [ excuseDuration, setExcuseDuration ] = React.useState<string>('');

  const handleExcuseSubmission = () => {
    setExcuseLoading(true);
    api.post('/events/excuses/create', {
      content: excuseValue,
      eventId: eventData.id,
      date: dayjs.utc().toISOString(),
      duration: excuseDuration,
    })
      .then((res) => {
        console.log(res);
        // reset excuse area in case component is being reloading with new task data
        setShowExcuseForm(false);
        setExcuseValue('');
        
        moveToNextTaskCallback();
      })
      .catch(console.log)
      .finally(() => {
        setExcuseLoading(false);
        setExcuseDuration('');
      })
  }

  const handleComplete = () => {
    setCompleteLoading(true);
    api.put(`/events/update/${eventData.id}`, {
      completed: true
    })
      .then((res) => {
        console.log(res);
        moveToNextTaskCallback();
      })
      .catch(console.log)
      .finally(() => {
        setCompleteLoading(false);
        setExcuseDuration('');
      });
  }

  const actions = (
    <React.Fragment>
      <Button
        variant='contained'
        loading={completeLoading}
        onClick={handleComplete}
        sx={{ width: isMobile ? '100%' : 'auto', mb: isMobile ? 1 : 0 }}
      >
        <DoneIcon sx={{ pr: 1 }} /> Complete
      </Button>
    </React.Fragment>
  )

  return (
    <Card sx={{ width: '100%', m: 0, p: 2, pt: isMobile ? 0 : 2, minHeight: 'auto', border: isMobile ? 'none' : '' }} variant='outlined'>
      <CardHeader
        title={
          <React.Fragment>
            <EventIcon sx={{
              mb: '-4px',
              mr: '3px'
            }} />{eventData.title}
          </React.Fragment>
        }
        subheader={
          <React.Fragment>
            <ItemContentDate itemData={eventData} itemType="event" />

            <FlagIcon sx={{
              height: '18px',
              width: '20px',
              marginBottom: '-4px',
              ml: '4px'
            }} />{convertPriorityToString(eventData.priority)}
          </React.Fragment>
        }
        action={ isMobile ? null : actions }
      />
      <CardContent sx={{ pt: 0 }}>
        <ItemBreadcrumbs
          currentItem={eventData}
          itemType="event"
          includeCurrentItem={true}
        />

        { eventData.description &&
          <Typography>{eventData.description.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {line}
              <br />
            </React.Fragment>
          ))}<br /></Typography>
        }

        {/* show action buttons here on mobile */}
        { isMobile && actions }

        <ListItemButton onClick={() => setShowExcuseForm(!showExcuseForm)} sx={{ color: 'grey', width: '100%', mt: 2 }}>
          {showExcuseForm ? <KeyboardArrowDownIcon sx={{ pr: 1 }} /> : <KeyboardArrowUpIcon sx={{ pr: 1 }} />} Cant Complete / Don't Want To
        </ListItemButton>
        <Collapse in={showExcuseForm}>
          <TextField
            label="Excuse"
            value={excuseValue}
            onChange={(event) => setExcuseValue(event.target.value)}
            sx={{ paddingBottom: 2, mt: 2 }}
            multiline
            rows={4}
            fullWidth
          />

          <ExcuseDurationSelect
            onChange={(intervalString: string) => setExcuseDuration(intervalString)}
          />
          
          <Button
            loading={excuseLoading}
            onClick={handleExcuseSubmission}
            disabled={!excuseDuration || !excuseValue}
            variant='outlined'
            fullWidth
          >
            <ClearIcon sx={{ pr: 1}} /> Submit Excuse 
          </Button>
        </Collapse>
      </CardContent>
    </Card>
  );
}